import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { getIndex, getStatistics, listProducts } from "@/lib/api";
import type { IndexPoint, IndexStatistics, MpInfo } from "@/types/api";

// localStorage key — value is a JSON array of mp_codes the user has *excluded*.
// We store the exclusion set (not the inclusion set) so newly added products
// default to visible — the alternative would silently hide new mp_codes from
// returning users until they remember to tick them.
const STORAGE_KEY = "emp_web.products.excluded";

function loadExcluded(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function saveExcluded(excluded: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(excluded)));
  } catch {
    // quota / private-mode failures are harmless — just lose persistence.
  }
}

// ────────────────────────────────────────────────────────────────── helpers
const pct = (v: number | null | undefined): string =>
  v === null || v === undefined ? "—" : `${(v * 100).toFixed(2)}%`;

const fmtDate = (d: string | null | undefined): string => d ?? "—";

function signClass(v: number | null | undefined): string {
  if (v === null || v === undefined) return "text-ink-dim";
  if (v > 0) return "text-up";
  if (v < 0) return "text-down";
  return "text-ink-mute";
}

// Pull the "current" (latest non-null) TR series from an IndexPoint[]. We
// prefer inception → falls back to monitoring for products where ops only
// publishes the monitoring window.
function pickTrSeries(rows: IndexPoint[]): { dt: string; tr: number }[] {
  return rows
    .map((r) => ({
      dt: r.dt,
      tr: r.tr_index_inception ?? r.tr_index_monitoring,
    }))
    .filter((r): r is { dt: string; tr: number } => r.tr !== null && r.tr !== undefined);
}

// Today / yesterday TR delta, for the SummaryIndex "current index" line.
function indexHeadline(series: { dt: string; tr: number }[]): {
  index: string; change: string; sign: "▲" | "▼" | "—"; changePct: string; raw: number;
} {
  if (series.length < 2) return { index: "—", change: "—", sign: "—", changePct: "—", raw: 0 };
  const last = series[series.length - 1].tr;
  const prev = series[series.length - 2].tr;
  const change = last - prev;
  const sign = change > 0 ? "▲" : change < 0 ? "▼" : "—";
  const pctV = (change / last) * 100;
  return {
    index: last.toFixed(2),
    change: Math.abs(change).toFixed(2),
    sign,
    changePct: `${pctV.toFixed(2)}%`,
    raw: change,
  };
}

// ──────────────────────────────────────────────────────── component: list page
export function ProductListPage() {
  const [products, setProducts]   = useState<MpInfo[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [excluded, setExcluded]   = useState<Set<string>>(() => loadExcluded());

  useEffect(() => {
    let cancelled = false;
    listProducts()
      .then((d) => { if (!cancelled) setProducts(d); })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.detail ?? e?.message ?? "Request failed";
        setListError(msg);
      });
    return () => { cancelled = true; };
  }, []);

  // Persist on every change. Cheap — the set is at most a few dozen items.
  useEffect(() => { saveExcluded(excluded); }, [excluded]);

  // Group by product_line for visual grouping. mutalisk had a flat list but
  // with 40+ products grouping makes the page scannable.
  const grouped = useMemo(() => {
    if (!products) return null;
    const map = new Map<string, MpInfo[]>();
    for (const p of products) {
      const k = p.product_line ?? "—";
      const arr = map.get(k) ?? [];
      arr.push(p);
      map.set(k, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [products]);

  // ─── filter helpers (closures over excluded state)
  const toggle = (mp: string) => setExcluded((prev) => {
    const next = new Set(prev);
    if (next.has(mp)) next.delete(mp); else next.add(mp);
    return next;
  });
  const setGroup = (codes: string[], include: boolean) => setExcluded((prev) => {
    const next = new Set(prev);
    for (const c of codes) {
      if (include) next.delete(c); else next.add(c);
    }
    return next;
  });
  const selectAll = () => setExcluded(new Set());
  const clearAll  = () => setExcluded(new Set(products?.map((p) => p.mp_code) ?? []));

  if (listError) return <ErrBox msg={listError} />;
  if (!products) return <p className="text-header">Loading products…</p>;
  if (products.length === 0) {
    return <p className="text-header">No products found — your account has no mp_code grants.</p>;
  }

  const totalVisible = products.length - excluded.size;

  return (
    <div className="space-y-6">
      {/* ───── filter dropdown */}
      <div className="flex justify-end">
        <FilterDropdown
          grouped={grouped!}
          excluded={excluded}
          totalVisible={totalVisible}
          totalProducts={products.length}
          onToggle={toggle}
          onGroup={setGroup}
          onSelectAll={selectAll}
          onClearAll={clearAll}
        />
      </div>

      {/* ───── cards */}
      <div className="space-y-8">
        {grouped!.map(([line, items]) => {
          const visibleItems = items.filter((p) => !excluded.has(p.mp_code));
          if (visibleItems.length === 0) return null;
          return (
            <section key={line}>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-sm uppercase tracking-wider text-header">{line}</h2>
                <span className="text-xs text-header">
                  {visibleItems.length}/{items.length} shown
                </span>
              </div>
              <div className="space-y-3">
                {visibleItems.map((p) => <ProductCard key={p.mp_code} mp={p} />)}
              </div>
            </section>
          );
        })}
        {totalVisible === 0 && (
          <p className="text-header text-sm">All products hidden by the filter.</p>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────── filter dropdown
function FilterDropdown({
  grouped, excluded, totalVisible, totalProducts,
  onToggle, onGroup, onSelectAll, onClearAll,
}: {
  grouped: [string, MpInfo[]][];
  excluded: Set<string>;
  totalVisible: number;
  totalProducts: number;
  onToggle: (mp: string) => void;
  onGroup: (codes: string[], include: boolean) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on click-outside / Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          "flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm " +
          (open
            ? "bg-panel-2 border-accent text-ink"
            : "bg-panel border-line text-ink-mute hover:text-ink hover:border-ink-mute")
        }
        aria-expanded={open}
      >
        <span className="text-xs uppercase tracking-wider text-ink-dim">Filter</span>
        <span className="text-ink">{totalVisible}<span className="text-ink-dim">/{totalProducts}</span></span>
        <span className={"text-ink-dim transition-transform " + (open ? "rotate-180" : "")}>▾</span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 z-20 rounded-md bg-panel border border-line/60 shadow-xl p-3"
          role="dialog"
        >
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={onSelectAll}
              className="flex-1 text-xs px-2 py-1 rounded border border-line text-ink-mute hover:text-ink hover:border-ink-mute"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={onClearAll}
              className="flex-1 text-xs px-2 py-1 rounded border border-line text-ink-mute hover:text-ink hover:border-ink-mute"
            >
              Clear all
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1 space-y-3">
            {grouped.map(([line, items]) => {
              const codes = items.map((p) => p.mp_code);
              const includedCount = codes.filter((c) => !excluded.has(c)).length;
              const groupState: "all" | "none" | "some" =
                includedCount === 0 ? "none" :
                includedCount === codes.length ? "all" :
                "some";
              return (
                <div key={line}>
                  <label className="flex items-center gap-2 cursor-pointer select-none py-1">
                    <TriCheckbox
                      state={groupState}
                      onChange={() => onGroup(codes, groupState !== "all")}
                    />
                    <span className="text-sm font-medium text-ink capitalize">{line}</span>
                  </label>
                  <ul className="pl-6 space-y-0.5 mt-0.5">
                    {items.map((p) => (
                      <li key={p.mp_code}>
                        <label className="flex items-center gap-2 cursor-pointer select-none py-0.5">
                          <input
                            type="checkbox"
                            checked={!excluded.has(p.mp_code)}
                            onChange={() => onToggle(p.mp_code)}
                            className="h-3.5 w-3.5 accent-accent cursor-pointer"
                          />
                          <span className="font-mono text-xs text-ink-mute hover:text-ink">
                            {p.mp_code}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Tri-state checkbox driven by an enum (all | none | some). HTML's
// ``indeterminate`` is DOM-only, so we set it via a ref after each render.
function TriCheckbox({
  state, onChange,
}: { state: "all" | "none" | "some"; onChange: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === "all"}
      onChange={onChange}
      className="h-4 w-4 accent-accent cursor-pointer"
    />
  );
}

// ──────────────────────────────────────────────────────── component: card
function ProductCard({ mp }: { mp: MpInfo }) {
  const [stats,  setStats]  = useState<IndexStatistics | null>(null);
  const [series, setSeries] = useState<IndexPoint[] | null>(null);
  const [err,    setErr]    = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getStatistics(mp.mp_code), getIndex(mp.mp_code)])
      .then(([s, idx]) => {
        if (cancelled) return;
        setStats(s);
        setSeries(idx);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.response?.data?.detail ?? e?.message ?? "Request failed");
      });
    return () => { cancelled = true; };
  }, [mp.mp_code]);

  const tr = useMemo(() => (series ? pickTrSeries(series) : []), [series]);
  const head = indexHeadline(tr);
  const indexDate = tr.length > 0 ? tr[tr.length - 1].dt : "—";
  const ready = tr.length > 1;

  return (
    <Link
      to={`/products/${encodeURIComponent(mp.mp_code)}`}
      onClick={(e) => { if (!ready) e.preventDefault(); }}
      className={
        // Card stays at ~1072px regardless of frame width — w-[92%] gives a
        // responsive fallback on narrow viewports, max-w pins the absolute
        // size so future frame tweaks don't move the card with them.
        "block w-[92%] max-w-[1020px] mx-auto rounded-md bg-panel border border-line/60 px-6 py-9 transition-colors " +
        (ready ? "hover:border-accent/40 cursor-pointer" : "opacity-60 cursor-default")
      }
    >
      <div className="grid grid-cols-12 gap-6">
        {/* ───── left: title + summary index */}
        <div className="col-span-12 md:col-span-7 space-y-4">
          <div className="flex items-baseline justify-between">
            <div className="font-mono text-base font-semibold text-ink">
              {mp.mp_code.toUpperCase()}
            </div>
            {!ready && (
              <span className="text-xs text-ink-dim italic">no NAV data yet</span>
            )}
          </div>

          {!ready ? (
            <p className="text-sm text-ink-dim">This product is not ready yet.</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
              <SumRow label="Index" value={
                <span className="font-mono">
                  <span className="text-ink">{head.index}</span>
                  <span className={"ml-2 " + signClass(head.raw)}>
                    {head.sign} {head.change} ({head.changePct})
                  </span>
                </span>
              }/>
              <SumRow label="Inception"     value={fmtDate(mp.inception_date)} />
              <SumRow label="Updated as of" value={indexDate} />
              <SumRow label="Period"        value={stats?.period_start && stats?.period_end
                ? `${stats.period_start} → ${stats.period_end}`
                : "—"} />
            </div>
          )}

          {ready && stats && <ProfitGrid s={stats} />}
        </div>

        {/* ───── right: mini chart */}
        <div className="col-span-12 md:col-span-5">
          <div className="h-32 md:h-36">
            {tr.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={tr} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`g-${mp.mp_code}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"  stopColor="#5977e2" stopOpacity={0.55}/>
                      <stop offset="100%" stopColor="#5977e2" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="dt" hide />
                  <YAxis hide domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{ background: "#1f2937", border: "1px solid #374151", color: "#f3f4f6", fontSize: 13 }}
                    formatter={(v: number) => v.toFixed(2)}
                  />
                  <Area
                    type="monotone"
                    dataKey="tr"
                    stroke="#5977e2"
                    fill={`url(#g-${mp.mp_code})`}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : err ? (
              <div className="h-full grid place-items-center text-xs text-down">{err}</div>
            ) : (
              <div className="h-full grid place-items-center text-xs text-ink-dim">loading…</div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

// ──────────────────────────────────────────────────────── component: rows
function SumRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between border-b border-line/40 py-1">
      <span className="text-ink-mute">{label}</span>
      <span className="text-right text-ink">{value}</span>
    </div>
  );
}

function ProfitGrid({ s }: { s: IndexStatistics }) {
  const cells: [string, number | null][] = [
    ["1M",  s.return_1m],
    ["3M",  s.return_3m],
    ["6M",  s.return_6m],
    ["1Y",  s.return_1y],
    ["3Y",  s.return_3y],
    ["5Y",  s.return_5y],
    ["YTD", s.ytd],
    ["ITD", s.itd],
  ];
  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-1 text-sm pt-2">
      {cells.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between">
          <span className="text-ink-mute">{k}</span>
          <span className={"font-mono " + signClass(v)}>{pct(v)}</span>
        </div>
      ))}
    </div>
  );
}

function ErrBox({ msg }: { msg: string }) {
  return (
    <div className="rounded-md bg-down/10 border border-down/30 px-3 py-2 text-sm text-down">
      {msg}
    </div>
  );
}
